import React, { useEffect, useState } from 'react'
import { Flex, H2, Line } from '../../lib/components'
import { tradingAdminServices } from '../../services'
import { InstantFundingReconciliation } from './instant-funding-reconciliation'
import { JournalReconciliation } from './journal-reconciliation'
import { TransfersNeedingAttention } from './transfers-needing-attention'
import '../wallet-topups/wallet-admin.scss'
import './trading-admin.scss'

type AccountRow = {
  id: number
  investor_id: number
  alpaca_account_id: string
  account_number: string | null
  status: string
  created_at: string
}

type OrderRow = {
  id: number
  investor_id: number
  alpaca_order_id: string
  symbol: string
  side: string
  type: string
  qty: string | null
  notional: string | null
  limit_price: string | null
  status: string
  filled_qty: string | null
  filled_avg_price: string | null
  created_at: string
}

const fmtDate = (value: string) => new Date(value).toLocaleString()

type FundingConfig = {
  mode: 'prefunding' | 'instant_funding'
  floatAccountConfigured: boolean
  withdrawalAccountConfigured: boolean
  instantFundingConfigured: boolean
}

const MODE_LABELS = {
  prefunding: 'Pre-funding (journals from the firm float)',
  instant_funding: 'Instant funding (settled with Alpaca T+1)',
}

const TradingAdminContainer = () => {
  const [accounts, setAccounts] = useState<AccountRow[]>([])
  const [orders, setOrders] = useState<OrderRow[]>([])
  const [error, setError] = useState('')
  const [funding, setFunding] = useState<FundingConfig | null>(null)

  useEffect(() => {
    void tradingAdminServices.getFundingConfig().then((res: any) => {
      if (res?.status === 200) setFunding(res.data)
    })
  }, [])

  useEffect(() => {
    const load = async () => {
      const [a, o] = await Promise.all([
        tradingAdminServices.listAccounts(),
        tradingAdminServices.listOrders(),
      ])
      if (a?.status === 200) setAccounts(a.data)
      if (o?.status === 200) setOrders(o.data)
      if (a?.status !== 200 || o?.status !== 200) setError('Could not load trading data.')
    }
    void load()
  }, [])

  return (
    <Flex variant='column' classes={['wallet-admin']}>
      {funding ? (
        <p className='funding-recon__mode'>
          Funding mode: <strong>{MODE_LABELS[funding.mode]}</strong>
          {funding.mode === 'prefunding' && !funding.floatAccountConfigured
            ? ' · ALPACA_FLOAT_ACCOUNT_ID is not set'
            : ''}
          {!funding.withdrawalAccountConfigured ? ' · Withdrawals are not configured' : ''}
        </p>
      ) : null}

      {funding?.mode === 'instant_funding' ? (
        <>
          <InstantFundingReconciliation />
          <Line />
        </>
      ) : null}

      {funding ? (
        <>
          <TransfersNeedingAttention />
          <Line />
          <JournalReconciliation showFloat={funding.mode === 'prefunding'} />
        </>
      ) : null}

      <Line />
      <H2 text='Brokerage accounts' classes={['mb-24']} />
      {error ? <p className='wallet-admin__empty'>{error}</p> : null}
      {accounts.length === 0 ? (
        <p className='wallet-admin__empty'>No brokerage accounts yet.</p>
      ) : (
        <div className='w-100 table_scroll_horizontal'>
          <table className='styled-table'>
            <thead>
              <tr>
                <th>Investor</th>
                <th>Account no.</th>
                <th>Alpaca ID</th>
                <th>Status</th>
                <th>Opened</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((row) => (
                <tr key={row.id}>
                  <td>{row.investor_id}</td>
                  <td>{row.account_number || '—'}</td>
                  <td>{row.alpaca_account_id}</td>
                  <td>{row.status}</td>
                  <td>{fmtDate(row.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Line />
      <H2 text='Stock orders' classes={['mb-24']} />
      {orders.length === 0 ? (
        <p className='wallet-admin__empty'>No orders yet.</p>
      ) : (
        <div className='w-100 table_scroll_horizontal'>
          <table className='styled-table'>
            <thead>
              <tr>
                <th>Investor</th>
                <th>Symbol</th>
                <th>Side</th>
                <th>Type</th>
                <th>Size</th>
                <th>Status</th>
                <th>Filled</th>
                <th>Placed</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((row) => (
                <tr key={row.id}>
                  <td>{row.investor_id}</td>
                  <td>{row.symbol}</td>
                  <td>{row.side}</td>
                  <td>
                    {row.type}
                    {row.limit_price ? ` @ ${row.limit_price}` : ''}
                  </td>
                  <td>{row.qty ? `${Number(row.qty)} sh` : `$${row.notional}`}</td>
                  <td>{row.status}</td>
                  <td>
                    {row.filled_qty && Number(row.filled_qty) > 0
                      ? `${Number(row.filled_qty)} @ ${row.filled_avg_price}`
                      : '—'}
                  </td>
                  <td>{fmtDate(row.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Flex>
  )
}

export { TradingAdminContainer }
